import React, { useState } from 'react';
import { Form, Button, Alert, InputGroup } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import PublicPageLayout from './PublicPageLayout';
import { landingPath } from '../utils/permissionUtils';
import { TwoFactorSetupPanel, RecoveryCodesPanel } from './TwoFactorPanels';

function Login() {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [needsVerification, setNeedsVerification] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // After a right password the second step: 'code' (the app's code), 'setup' (the organization requires two-step and this person has none yet), then 'recovery'
  const [step, setStep] = useState('password');
  const [challenge, setChallenge] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [code, setCode] = useState('');
  const [remember, setRemember] = useState(false);
  const [finished, setFinished] = useState(null);

  const { login, verifyTwoFactor, startTwoFactorSetup, finishTwoFactorSetup, finishSignIn } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setNeedsVerification(false);

    const result = await login(formData.email, formData.password);

    if (result.success) {
      toast.success('Login successful!');
      navigate(landingPath(result.user));
    } else if (result.twoFactor) {
      setChallenge(result.twoFactor.challenge);
      setOrganizationName(result.twoFactor.organizationName || '');
      setCode('');
      setStep(result.twoFactor.twoFactorSetupRequired ? 'setup' : 'code');
    } else {
      setError(result.error);
      toast.error('Login failed');
    }

    setNeedsVerification(
      typeof result.error === 'string' && result.error.toLowerCase().includes('verify your email')
    );

    setLoading(false);
  };

  const backToPassword = (message) => {
    setStep('password');
    setChallenge('');
    setCode('');
    setFinished(null);
    setError(message || '');
  };

  const handleCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const result = await verifyTwoFactor(challenge, code, remember, formData.email);
    setLoading(false);
    if (result.success) {
      if (result.data.recoveryCodesLeft !== undefined) {
        toast.warn(`You signed in with a recovery code. ${result.data.recoveryCodesLeft} left; make new ones in Settings.`);
      }
      const user = finishSignIn(result.data);
      navigate(landingPath(user));
    } else if (result.status === 401) {
      backToPassword(result.error);
    } else {
      setError(result.error);
      setCode('');
    }
  };

  if (step === 'code') {
    return (
      <PublicPageLayout subtitle="Two-step sign-in">
        {error && <Alert variant="danger">{error}</Alert>}
        <Form onSubmit={handleCode}>
          <p className="text-muted small">Open your authenticator app and type the 6-digit code it shows for iBigDo.</p>
          <Form.Group className="mb-3">
            <Form.Label>Code</Form.Label>
            <Form.Control
              type="text"
              inputMode="text"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="123456"
              data-testid="two-factor-code"
              required
              autoFocus
            />
            <Form.Text className="text-muted">Lost your phone? Type one of your recovery codes here instead.</Form.Text>
          </Form.Group>
          <Form.Check
            type="checkbox"
            id="remember-browser"
            className="mb-3"
            label="Don't ask for a code on this browser for 30 days"
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
          />
          <Button variant="primary" type="submit" className="w-100" disabled={loading || !code.trim()} data-testid="two-factor-submit">
            {loading ? 'Checking...' : 'Sign In'}
          </Button>
        </Form>
        <div className="text-center mt-3">
          <Button variant="link" size="sm" onClick={() => backToPassword()}>Back</Button>
        </div>
      </PublicPageLayout>
    );
  }

  if (step === 'setup') {
    return (
      <PublicPageLayout subtitle="Set up two-step sign-in">
        <Alert variant="info" className="py-2 small">
          {organizationName ? `"${organizationName}" asks everyone to` : 'Your organization asks everyone to'} use a code from an authenticator app as well as their password. It takes a minute.
        </Alert>
        <TwoFactorSetupPanel
          busyLabel="Turn on and sign in"
          start={async () => {
            const r = await startTwoFactorSetup(challenge);
            if (r.success) return r.data;
            if (r.status === 401) backToPassword(r.error);
            return { error: r.error };
          }}
          confirm={async (c) => {
            const r = await finishTwoFactorSetup(challenge, c, remember, formData.email);
            if (r.success) {
              setFinished(r.data);
              setStep('recovery');
              return null;
            }
            if (r.status === 401) backToPassword(r.error);
            return r.error;
          }}
          extra={(
            <Form.Check
              type="checkbox"
              id="remember-browser-setup"
              className="mb-3"
              label="Don't ask for a code on this browser for 30 days"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
            />
          )}
        />
        <div className="text-center mt-3">
          <Button variant="link" size="sm" onClick={() => backToPassword()}>Back</Button>
        </div>
      </PublicPageLayout>
    );
  }

  if (step === 'recovery' && finished) {
    return (
      <PublicPageLayout subtitle="Two-step sign-in is on">
        <RecoveryCodesPanel
          codes={finished.recoveryCodes || []}
          doneLabel="Continue to iBigDo"
          onDone={() => {
            const user = finishSignIn(finished);
            navigate(landingPath(user));
          }}
        />
      </PublicPageLayout>
    );
  }

  return (
    <PublicPageLayout subtitle="Sign in to your account">
      {error && (
        <Alert variant="danger">
          {error}
          {needsVerification && (
            <div className="mt-2">
              <Link to={`/verify-email?email=${encodeURIComponent(formData.email)}`}>
                Verify your email
              </Link>
            </div>
          )}
        </Alert>
      )}

      <Form onSubmit={handleSubmit}>
        <Form.Group className="mb-3">
          <Form.Label>Email</Form.Label>
          <Form.Control
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            placeholder="Enter email"
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Password</Form.Label>
          <InputGroup>
            <Form.Control
              type={showPassword ? "text" : "password"}
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              placeholder="Enter password"
            />
            <Button
              variant="outline-secondary"
              onClick={() => setShowPassword(!showPassword)}
              style={{ borderLeft: 'none' }}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? (
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M13.359 11.238C15.06 9.72 16 8 16 8s-3-5.5-8-5.5a7.028 7.028 0 0 0-2.79.588l.77.771A5.944 5.944 0 0 1 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.134 13.134 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755-.165.165-.337.328-.517.486l.708.709z"/>
                  <path d="M11.297 9.176a3.5 3.5 0 0 0-4.474-4.474l.823.823a2.5 2.5 0 0 1 2.829 2.829l.822.822zm-2.943 1.299.822.822a3.5 3.5 0 0 1-4.474-4.474l.823.823a2.5 2.5 0 0 0 2.829 2.829z"/>
                  <path d="M3.35 5.47c-.18.18-.35.377-.518.57l1.175 1.175a7.028 7.028 0 0 1 2.79-.588c2.12 0 3.879 1.168 5.168 2.457A13.134 13.134 0 0 1 14.828 8c.058.087.122.183.195.288.335.48.83 1.12 1.465 1.755.165.165.337.328.517.486l1.175 1.175a.5.5 0 0 1-.708.708l-1.175-1.175a13.07 13.07 0 0 1-1.755-1.465c-.105-.073-.2-.137-.288-.195L11.297 9.176a3.5 3.5 0 0 0-4.474-4.474L3.35 5.47zm-1.12 1.12L1.646 4.646a.5.5 0 0 1 .708-.708l1.175 1.175a13.07 13.07 0 0 1 1.755 1.465c.105.073.2.137.288.195L2.23 6.588z"/>
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z"/>
                  <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z"/>
                </svg>
              )}
            </Button>
          </InputGroup>
        </Form.Group>

        <div className="d-flex justify-content-end mb-3">
          <Link to="/forgot-password">Forgot password?</Link>
        </div>

        <Button
          variant="primary"
          type="submit"
          className="w-100"
          disabled={loading}
        >
          {loading ? 'Signing in...' : 'Sign In'}
        </Button>
      </Form>

      <div className="text-center mt-3">
        <a href="/guide/getting-started.html" target="_blank" rel="noopener noreferrer">
          <i className="bi bi-book me-1" />
          New to iBigDo? Read the Getting Started guide
        </a>
      </div>
    </PublicPageLayout>
  );
}

export default Login;
